/**
 * FeedbackWidget — Floating button (bottom-right) that opens a compact NPS+message form.
 * Posts to /api/feedback. Includes current page URL for context.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MessageSquareHeart, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (rating === null && !message.trim()) {
      toast.info("Bewertung oder Nachricht erforderlich");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/feedback`,
        {
          rating,
          message,
          page: window.location.pathname,
          category: "app_feedback",
        },
        { withCredentials: true }
      );
      toast.success("Danke fuer dein Feedback!");
      setOpen(false);
      setRating(null);
      setMessage("");
    } catch (e) {
      toast.error("Fehler beim Senden");
    }
    setSending(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-widget-btn"
        className="fixed bottom-4 right-4 z-40 bg-[#0F0F0F] hover:bg-[#2563EB] text-white rounded-full w-12 h-12 flex items-center justify-center shadow-xl transition-all hover:scale-110"
        title="Feedback geben"
      >
        <MessageSquareHeart className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Dein Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-[var(--studio-text-muted)] mb-2">
                Wie wahrscheinlich empfiehlst du InvictaFlow weiter? (0-10)
              </p>
              <div className="flex gap-1" data-testid="feedback-rating-row">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    data-testid={`feedback-rating-${n}`}
                    className={`flex-1 h-9 rounded text-xs font-medium border transition-all ${
                      rating === n
                        ? "bg-[#2563EB] text-white border-[#2563EB]"
                        : "bg-white border-[var(--studio-border)] hover:border-[#2563EB]"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-[var(--studio-text-muted)] mt-1">
                <span>Unwahrscheinlich</span>
                <span>Sehr wahrscheinlich</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-[var(--studio-text-muted)] mb-1">Kommentar (optional)</p>
              <Textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Was gefaellt dir? Was fehlt noch?"
                data-testid="feedback-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="feedback-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={submit}
              disabled={sending}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="feedback-send"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
